/**
 * SavedMessagesDrawer
 *
 * Drawer lateral com mensagens prontas que o operador pode inserir no chat.
 * Duas sessoes navegaveis no header:
 *   - "Minhas mensagens" — mensagens salvas pelo proprio operador (CRUD em
 *     /saved-messages). Clicar em "Usar" envia o texto/anexos para o input
 *     do chat via callback onUseMessage(item).
 *   - "Templates Meta" — visivel APENAS no WhatsApp Business. Lista templates
 *     aprovados pela Meta e permite disparar para o contato atual com 1
 *     clique (chama templateService.send).
 *
 * Padrao visual segue LabelPickerModal/ScheduleMessageModal em displayMode
 * 'drawer' (420px de largura, encostado a direita, header com close).
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  X, BookmarkPlus, FileText, Search, Send, Loader2,
  Paperclip, Mic, Check, AlertCircle,
} from 'lucide-react';
import savedMessagesService from '../../services/global/savedMessagesService';
import templateService from '../../services/global/templateService';
import TemplateFillPanel from './TemplateFillPanel';
import { templateRequiresParams, fillBodyText } from '../../utils/templateVariables';
import { analyzeTemplate } from '../../utils/templateParams';
import useFeatureFlags from '../../hooks/useFeatureFlags';

const TAB_SAVED = 'saved';
const TAB_TEMPLATES = 'templates';

const SavedMessagesDrawer = ({
  isOpen,
  onClose,
  colors,
  contactPhone,
  contactName,
  // 'wa_personal' | 'wa_business' — controla a aba de templates Meta.
  channel,
  // Inserir mensagem salva no input do chat. Recebe o item completo
  // (text + attachments + audio) — quem decide o que fazer e o caller.
  onUseMessage,
  // Disparado apos enviar um template Meta com sucesso. O caller usa para
  // fazer optimistic update no chat — antes o operador precisava trocar de
  // chat para a mensagem aparecer (so chegava via webhook da Meta com algum
  // atraso). Payload: { templateName, templateBody, templateLanguage, wamid }
  onMessageSent,
  // 'saved' (default) | 'templates'. Permite o caller abrir direto na aba
  // de Templates Meta — usado pelos atalhos de "fora da janela 24h", onde
  // o template e a unica opcao valida e o operador nao deveria precisar
  // clicar uma aba extra.
  initialTab = 'saved',
}) => {
  const isBusinessChannel = channel === 'wa_business';

  const [activeTab, setActiveTab] = useState(initialTab === 'templates' && channel === 'wa_business' ? TAB_TEMPLATES : TAB_SAVED);
  const [saved, setSaved] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedError, setSavedError] = useState('');

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');
  const [sendingTemplate, setSendingTemplate] = useState(null);
  const [sentTemplate, setSentTemplate] = useState(null);
  const [templateError, setTemplateError] = useState('');
  // Card em modo "preencher variáveis" (template com {{n}}).
  const [fillingName, setFillingName] = useState(null);

  const [search, setSearch] = useState('');
  // Editor único de variáveis (2026-09-14): sem a flag, tudo como antes.
  const { flags } = useFeatureFlags();
  const templateUi = Boolean(flags.template_params_ui);

  // Reset quando abre/fecha — evita o operador ver dados antigos do contato
  // anterior se o drawer e reaproveitado. Respeita initialTab para que o
  // caller possa abrir direto na aba de Templates Meta.
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSendingTemplate(null);
      setSentTemplate(null);
      setTemplateError('');
      return;
    }
    const wantsTemplates = initialTab === 'templates' && isBusinessChannel;
    setActiveTab(wantsTemplates ? TAB_TEMPLATES : TAB_SAVED);
  }, [isOpen, contactPhone, initialTab, isBusinessChannel]);

  // Carrega mensagens salvas quando abre. Carrega de novo entre aberturas
  // porque a lista pode ter mudado (operador pode ter criado via outra tela).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setSavedLoading(true);
    setSavedError('');
    savedMessagesService.list()
      .then((res) => {
        if (cancelled) return;
        setSaved(res?.items || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setSavedError(err?.message || 'Erro ao carregar mensagens salvas');
        setSaved([]);
      })
      .finally(() => {
        if (!cancelled) setSavedLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Templates so quando wa_business — evita request inutil no QR Code.
  useEffect(() => {
    if (!isOpen || !isBusinessChannel) return;
    let cancelled = false;
    setTemplatesLoading(true);
    setTemplatesError('');
    templateService.list()
      .then((data) => {
        if (cancelled) return;
        const list = data?.templates || data?.data || (Array.isArray(data) ? data : []) || [];
        const approved = list.filter((t) => (t.status || '').toUpperCase() === 'APPROVED');
        approved.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setTemplates(approved);
      })
      .catch((err) => {
        if (cancelled) return;
        setTemplatesError(err?.response?.data?.detail || err?.message || 'Erro ao carregar templates');
        setTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, isBusinessChannel]);

  // Filtro de busca por titulo + texto (saved) e nome do template.
  const filteredSaved = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return saved;
    return saved.filter((m) =>
      (m.title || '').toLowerCase().includes(q) ||
      (m.text || '').toLowerCase().includes(q)
    );
  }, [saved, search]);

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.components || []).some((c) => (c.text || '').toLowerCase().includes(q))
    );
  }, [templates, search]);

  const handleUseSaved = (item) => {
    if (onUseMessage) onUseMessage(item);
    onClose?.();
  };

  const handleSendTemplate = async (template, extra = null) => {
    if (!contactPhone) return;
    setSendingTemplate(template.name);
    setTemplateError('');
    try {
      const result = await templateService.send(template.name, contactPhone, extra || {});
      setSentTemplate(template.name);
      setFillingName(null);

      // Optimistic update no chat — passa o corpo resolvido e o wamid
      // retornado pela Meta. O ChatWindow injeta a mensagem na lista
      // imediatamente e o webhook posterior cai em dedup pelo wamid.
      if (onMessageSent) {
        onMessageSent({
          templateName: template.name,
          // Com o editor novo os valores podem ser {{primeiro_nome}}: a bolha
          // usa o corpo já resolvido que a rota devolve.
          templateBody: templateUi && result?.resolved_body_text
            ? result.resolved_body_text
            : extra?.body_params
              ? fillBodyText(getTemplateBody(template), extra.body_params)
              : getTemplateBody(template),
          templateLanguage: template.language || 'pt_BR',
          wamid: result?.message_id || null,
        });
      }

      // Feedback visual de sucesso some apos 2s — o operador ve o check
      // verde e o drawer continua aberto caso queira enviar outro.
      setTimeout(() => setSentTemplate((cur) => (cur === template.name ? null : cur)), 2500);
    } catch (err) {
      setTemplateError(err?.response?.data?.detail || err?.message || 'Erro ao enviar template');
    } finally {
      setSendingTemplate(null);
    }
  };

  if (!isOpen) return null;

  const getTemplateBody = (t) => {
    const body = (t.components || []).find((c) => (c.type || '').toUpperCase() === 'BODY');
    return body?.text || '';
  };

  const tabBtnStyle = (key) => ({
    flex: 1,
    padding: '10px 12px',
    border: 'none',
    background: 'transparent',
    borderBottom: `2px solid ${activeTab === key ? colors.accentPrimary : 'transparent'}`,
    color: activeTab === key ? colors.accentPrimary : colors.textSecondary,
    fontSize: 13,
    fontWeight: activeTab === key ? 600 : 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    transition: 'all 0.15s ease',
  });

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: colors.bgPrimary,
          width: 420, maxWidth: '100%', height: '100%',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.25)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
          flexShrink: 0,
        }}>
          <div>
            <h3 style={{
              margin: 0, fontSize: 16, fontWeight: 600, color: colors.textPrimary,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <BookmarkPlus size={18} color={colors.accentPrimary} />
              Mensagens Prontas
            </h3>
            {(contactName || contactPhone) && (
              <p style={{ margin: '2px 0 0', fontSize: 12, color: colors.textSecondary }}>
                Para: {contactName || contactPhone}
              </p>
            )}
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: colors.textSecondary, padding: 8, borderRadius: 8,
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs — so renderiza a divisao quando ha WA Business para a aba
            de templates fazer sentido. No QR Code mostramos so a lista
            simples sem header de abas. */}
        {isBusinessChannel && (
          <div style={{
            display: 'flex', borderBottom: `1px solid ${colors.borderColor}`,
            flexShrink: 0,
          }}>
            <button style={tabBtnStyle(TAB_SAVED)} onClick={() => setActiveTab(TAB_SAVED)}>
              <BookmarkPlus size={14} />
              Minhas mensagens
            </button>
            <button style={tabBtnStyle(TAB_TEMPLATES)} onClick={() => setActiveTab(TAB_TEMPLATES)}>
              <FileText size={14} />
              Templates Meta
            </button>
          </div>
        )}

        {/* Search */}
        <div style={{
          padding: '12px 20px 8px', flexShrink: 0,
          borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', borderRadius: 8,
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.borderColor}`,
          }}>
            <Search size={14} color={colors.textSecondary} />
            <input
              type="text"
              placeholder={activeTab === TAB_TEMPLATES ? 'Buscar template…' : 'Buscar mensagem…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: 1, border: 'none', outline: 'none', background: 'transparent',
                fontSize: 13, color: colors.textPrimary,
              }}
            />
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          {activeTab === TAB_SAVED && (
            <SavedList
              items={filteredSaved}
              loading={savedLoading}
              error={savedError}
              colors={colors}
              onUse={handleUseSaved}
              emptyHint={
                saved.length === 0
                  ? 'Nenhuma mensagem salva ainda. Crie em "Mensagens Salvas" no menu lateral.'
                  : 'Nenhuma mensagem encontrada para essa busca.'
              }
            />
          )}

          {activeTab === TAB_TEMPLATES && (
            <TemplatesList
              items={filteredTemplates}
              loading={templatesLoading}
              error={templatesError}
              colors={colors}
              sendingTemplate={sendingTemplate}
              sentTemplate={sentTemplate}
              onSend={handleSendTemplate}
              getTemplateBody={getTemplateBody}
              hasContact={!!contactPhone}
              contactName={contactName}
              contactPhone={contactPhone}
              templateUi={templateUi}
              fillingName={fillingName}
              onStartFill={(name) => { setFillingName(name); setTemplateError(''); }}
              onCancelFill={() => setFillingName(null)}
              emptyHint={
                templates.length === 0
                  ? 'Nenhum template aprovado. Crie em "Templates" no menu do WhatsApp Business.'
                  : 'Nenhum template encontrado para essa busca.'
              }
            />
          )}

          {templateError && activeTab === TAB_TEMPLATES && (
            <div style={{
              marginTop: 12, padding: 10, borderRadius: 8,
              backgroundColor: `${colors.danger || '#ef4444'}15`,
              color: colors.danger || '#ef4444',
              fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <AlertCircle size={14} /> {templateError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SavedList = ({ items, loading, error, colors, onUse, emptyHint }) => {
  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 40, color: colors.textSecondary, gap: 8,
      }}>
        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
        Carregando…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{
        padding: 12, borderRadius: 8,
        backgroundColor: `${colors.danger || '#ef4444'}15`,
        color: colors.danger || '#ef4444', fontSize: 13,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <AlertCircle size={14} /> {error}
      </div>
    );
  }
  if (!items.length) {
    return (
      <div style={{
        padding: 24, textAlign: 'center', color: colors.textSecondary, fontSize: 13,
      }}>
        {emptyHint}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((m) => (
        <div key={m.id} style={{
          padding: 12, borderRadius: 10,
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.borderColor}`,
        }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: colors.textPrimary, marginBottom: 4,
          }}>
            {m.title || 'Sem título'}
          </div>
          {m.text && (
            <div style={{
              fontSize: 12, color: colors.textSecondary, marginBottom: 8,
              lineHeight: 1.4, whiteSpace: 'pre-wrap',
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {m.text}
            </div>
          )}
          {(m.attachments?.length > 0 || m.audio) && (
            <div style={{
              display: 'flex', gap: 6, marginBottom: 8, fontSize: 11,
              color: colors.textTertiary,
            }}>
              {m.attachments?.length > 0 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Paperclip size={11} /> {m.attachments.length}
                </span>
              )}
              {m.audio && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Mic size={11} /> audio
                </span>
              )}
            </div>
          )}
          <button
            onClick={() => onUse(m)}
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 8, border: 'none',
              backgroundColor: colors.accentPrimary, color: '#fff',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            <Send size={12} /> Usar mensagem
          </button>
        </div>
      ))}
    </div>
  );
};

const TemplatesList = ({
  items, loading, error, colors, sendingTemplate, sentTemplate,
  onSend, getTemplateBody, hasContact, emptyHint,
  contactName, fillingName, onStartFill, onCancelFill,
  contactPhone, templateUi = false,
}) => {
  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 40, color: colors.textSecondary, gap: 8,
      }}>
        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
        Carregando templates…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{
        padding: 12, borderRadius: 8,
        backgroundColor: `${colors.danger || '#ef4444'}15`,
        color: colors.danger || '#ef4444', fontSize: 13,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <AlertCircle size={14} /> {error}
      </div>
    );
  }
  if (!items.length) {
    return (
      <div style={{
        padding: 24, textAlign: 'center', color: colors.textSecondary, fontSize: 13,
      }}>
        {emptyHint}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((t) => {
        const body = getTemplateBody(t);
        const isSending = sendingTemplate === t.name;
        const isSent = sentTemplate === t.name;
        // Template com variável abre o formulário de preenchimento inline em
        // vez de enviar direto (a Meta recusa por parâmetro faltando).
        const needsParams = templateRequiresParams(t);
        const isFilling = fillingName === t.name;
        // Formato que a plataforma não monta: desabilitado com o motivo, em
        // vez de abrir um formulário que falharia no envio.
        const unsupported = templateUi ? analyzeTemplate(t).unsupported : null;
        return (
          <div key={t.name} style={{
            padding: 12, borderRadius: 10,
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.borderColor}`,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 8, marginBottom: 6,
            }}>
              <div style={{
                fontSize: 13, fontWeight: 600, color: colors.textPrimary,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {t.name}
              </div>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                backgroundColor: `${colors.accentPrimary}18`, color: colors.accentPrimary,
                textTransform: 'uppercase', letterSpacing: 0.4, flexShrink: 0,
              }}>
                {t.language || 'pt_BR'}
              </span>
            </div>
            {body && (
              <div style={{
                fontSize: 12, color: colors.textSecondary, marginBottom: 8,
                lineHeight: 1.4, whiteSpace: 'pre-wrap',
                display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
                {body}
              </div>
            )}
            {unsupported ? (
              <div style={{ fontSize: 11.5, color: colors.textTertiary, lineHeight: 1.4 }}>
                {unsupported}
              </div>
            ) : isFilling ? (
              <TemplateFillPanel
                template={t}
                colors={colors}
                contactName={contactName}
                contactPhone={contactPhone}
                sending={isSending}
                onSend={onSend}
                onCancel={onCancelFill}
              />
            ) : (
              <button
                onClick={() => (needsParams ? onStartFill(t.name) : onSend(t))}
                disabled={!hasContact || isSending || isSent}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 8, border: 'none',
                  backgroundColor: isSent
                    ? '#22c55e'
                    : (hasContact ? colors.accentPrimary : colors.bgTertiary),
                  color: hasContact || isSent ? '#fff' : colors.textTertiary,
                  fontSize: 12, fontWeight: 600,
                  cursor: hasContact && !isSending && !isSent ? 'pointer' : 'not-allowed',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  opacity: !hasContact ? 0.7 : 1,
                }}
                title={
                  !hasContact
                    ? 'Abra um contato para enviar'
                    : (needsParams
                      ? 'Este template tem variáveis: preencha antes de enviar'
                      : 'Enviar template')
                }
              >
                {isSending ? (
                  <>
                    <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    Enviando…
                  </>
                ) : isSent ? (
                  <>
                    <Check size={12} /> Enviado
                  </>
                ) : needsParams ? (
                  <>
                    <Send size={12} /> Preencher e enviar
                  </>
                ) : (
                  <>
                    <Send size={12} /> Enviar template
                  </>
                )}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SavedMessagesDrawer;
